import React, { useState, useEffect } from 'react';
import { 
  LayoutDashboard, 
  Users, 
  Bell, 
  Database, 
  BookOpen, 
  DollarSign, 
  Activity, 
  PlusCircle, 
  Trash2, 
  ShieldAlert, 
  CheckCircle2, 
  Search,
  Filter,
  ArrowUpRight,
  TrendingUp,
  RefreshCw,
  Calendar,
  Clock,
  ExternalLink
} from 'lucide-react';
import { getAdminAnalytics, getAdminUsersList, getAdminBreaches, getAdminAuditLogs, addAdminBreach } from '../api/admin';
import { AdminAnalytics, UserProfile, BreachRecord, AuditLogItem, SubscriptionPlan } from '../types';
import { useStore } from '../store/useStore';
import { RESOURCE_ARTICLES } from '../data/resources';
import { Link } from 'react-router-dom';

export const AdminDashboardPage: React.FC = () => {
  const { plans } = useStore();
  const [activeTab, setActiveTab] = useState<'analytics' | 'users' | 'breaches' | 'content' | 'pricing' | 'audit'>('analytics');
  
  const [analytics, setAnalytics] = useState<AdminAnalytics | null>(null);
  const [usersList, setUsersList] = useState<UserProfile[]>([]);
  const [breachesList, setBreachesList] = useState<BreachRecord[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLogItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [articleSearch, setArticleSearch] = useState('');

  // New Breach Modal
  const [showAddBreach, setShowAddBreach] = useState(false);
  const [newBreachOrg, setNewBreachOrg] = useState('');
  const [newBreachDomain, setNewBreachDomain] = useState('');
  const [newBreachDate, setNewBreachDate] = useState('2026-08-15');
  const [newBreachDesc, setNewBreachDesc] = useState('');
  const [newBreachClasses, setNewBreachClasses] = useState('Names, Email Addresses, Mobile Numbers');

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [an, us, br, au] = await Promise.all([
        getAdminAnalytics(),
        getAdminUsersList(),
        getAdminBreaches(),
        getAdminAuditLogs()
      ]);
      setAnalytics(an);
      setUsersList(us);
      setBreachesList(br);
      setAuditLogs(au);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleCreateBreach = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newBreachOrg.trim()) return;

    await addAdminBreach({
      name: newBreachOrg,
      organisation: newBreachOrg,
      domain: newBreachDomain || `${newBreachOrg.toLowerCase().replace(/\s+/g, '')}.com.au`,
      breachDate: newBreachDate,
      pwnCount: 150000,
      description: newBreachDesc || 'Unauthorized data extraction affecting customer accounts.',
      dataClasses: newBreachClasses.split(',').map(s => s.trim()),
      isVerified: true,
      isFabricated: false,
      isSensitive: true,
      isSpamList: false,
      country: 'Australia'
    });

    setShowAddBreach(false);
    setNewBreachOrg('');
    loadData();
  };

  const isFutureDate = (dateStr: string) => {
    try {
      const parsed = new Date(dateStr);
      const refDate = new Date('2026-08-20T23:59:59');
      return parsed > refDate;
    } catch {
      return false;
    }
  };

  const filteredContent = RESOURCE_ARTICLES.filter(a => {
    if (!articleSearch.trim()) return true;
    const q = articleSearch.toLowerCase();
    return a.title.toLowerCase().includes(q) || a.category.toLowerCase().includes(q) || a.slug.toLowerCase().includes(q);
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
      {/* Top Admin Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl">
        <div>
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-0.5 rounded-full bg-purple-950 text-purple-300 border border-purple-800 font-bold text-xs">
              ADMIN CONTROL PANEL
            </span>
            <span className="text-xs text-slate-400 font-mono">idfraud.au / sysadmin</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-white mt-1">Platform Operations & Telemetry</h1>
        </div>

        <button
          onClick={loadData}
          className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold flex items-center gap-2 self-start sm:self-auto transition-colors"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          <span>Refresh Telemetry</span>
        </button>
      </div>

      {/* Admin Nav Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-slate-800 pb-3 text-xs">
        <button
          onClick={() => setActiveTab('analytics')}
          className={`px-4 py-2 rounded-xl font-bold transition-colors flex items-center gap-1.5 ${
            activeTab === 'analytics' ? 'bg-purple-600 text-white' : 'bg-slate-900 text-slate-400 hover:text-white'
          }`}
        >
          <Activity className="w-3.5 h-3.5" />
          Analytics & KPIs
        </button>
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2 rounded-xl font-bold transition-colors flex items-center gap-1.5 ${
            activeTab === 'users' ? 'bg-purple-600 text-white' : 'bg-slate-900 text-slate-400 hover:text-white'
          }`}
        >
          <Users className="w-3.5 h-3.5" />
          Users ({usersList.length})
        </button>
        <button
          onClick={() => setActiveTab('content')}
          className={`px-4 py-2 rounded-xl font-bold transition-colors flex items-center gap-1.5 ${
            activeTab === 'content' ? 'bg-purple-600 text-white' : 'bg-slate-900 text-slate-400 hover:text-white'
          }`}
        >
          <BookOpen className="w-3.5 h-3.5" />
          Content & Editorial ({RESOURCE_ARTICLES.length})
        </button>
        <button
          onClick={() => setActiveTab('breaches')}
          className={`px-4 py-2 rounded-xl font-bold transition-colors flex items-center gap-1.5 ${
            activeTab === 'breaches' ? 'bg-purple-600 text-white' : 'bg-slate-900 text-slate-400 hover:text-white'
          }`}
        >
          <Database className="w-3.5 h-3.5" />
          Breaches DB ({breachesList.length})
        </button>
        <button
          onClick={() => setActiveTab('pricing')}
          className={`px-4 py-2 rounded-xl font-bold transition-colors flex items-center gap-1.5 ${
            activeTab === 'pricing' ? 'bg-purple-600 text-white' : 'bg-slate-900 text-slate-400 hover:text-white'
          }`}
        >
          <DollarSign className="w-3.5 h-3.5" />
          Pricing Plans Config
        </button>
        <button
          onClick={() => setActiveTab('audit')}
          className={`px-4 py-2 rounded-xl font-bold transition-colors flex items-center gap-1.5 ${
            activeTab === 'audit' ? 'bg-purple-600 text-white' : 'bg-slate-900 text-slate-400 hover:text-white'
          }`}
        >
          <Calendar className="w-3.5 h-3.5" />
          Audit Logs ({auditLogs.length})
        </button>
      </div>

      {/* Tab 1: Analytics Overview */}
      {activeTab === 'analytics' && analytics && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="p-5 rounded-2xl bg-slate-900 border border-slate-800 space-y-1">
              <span className="text-xs text-slate-400 font-medium">Registered Users</span>
              <div className="text-2xl sm:text-3xl font-extrabold text-white">{analytics.registeredUsers.toLocaleString()}</div>
              <span className="text-[10px] text-emerald-400 font-semibold">+14.2% this month</span>
            </div>

            <div className="p-5 rounded-2xl bg-slate-900 border border-slate-800 space-y-1">
              <span className="text-xs text-slate-400 font-medium">Monthly Recurring Revenue</span>
              <div className="text-2xl sm:text-3xl font-extrabold text-emerald-400">${analytics.subscriptions.mrrAud.toLocaleString()} AUD</div>
              <span className="text-[10px] text-slate-400 font-semibold">Churn: {analytics.subscriptions.churnRatePercent}%</span>
            </div>

            <div className="p-5 rounded-2xl bg-slate-900 border border-slate-800 space-y-1">
              <span className="text-xs text-slate-400 font-medium">Identity Exposure Checks</span>
              <div className="text-2xl sm:text-3xl font-extrabold text-white">{analytics.identityChecks.toLocaleString()}</div>
              <span className="text-[10px] text-blue-400 font-semibold">Live Searches</span>
            </div>

            <div className="p-5 rounded-2xl bg-slate-900 border border-slate-800 space-y-1">
              <span className="text-xs text-slate-400 font-medium">Scam & Threat Checks</span>
              <div className="text-2xl sm:text-3xl font-extrabold text-amber-400">{analytics.scamChecks.toLocaleString()}</div>
              <span className="text-[10px] text-slate-400 font-semibold">AI Inferences</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-6 rounded-2xl bg-slate-900 border border-slate-800 space-y-3 text-xs">
              <h3 className="font-bold text-white text-sm">Subscription Distribution</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-400">Free Tier:</span>
                  <span className="font-bold text-white">{analytics.subscriptions.free.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-emerald-400 font-semibold">Personal Plan ($9.95/mo):</span>
                  <span className="font-bold text-emerald-400">{analytics.subscriptions.personal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-purple-400 font-semibold">Family Plan ($19.95/mo):</span>
                  <span className="font-bold text-purple-400">{analytics.subscriptions.family.toLocaleString()}</span>
                </div>
              </div>
            </div>

            <div className="p-6 rounded-2xl bg-slate-900 border border-slate-800 space-y-3 text-xs">
              <h3 className="font-bold text-white text-sm">Alert Pipeline</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-400">Total Alerts Generated:</span>
                  <span className="font-bold text-white">{analytics.alertsGenerated.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-amber-400 font-semibold">Active Unresolved:</span>
                  <span className="font-bold text-amber-400">{analytics.activeAlerts.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-emerald-400 font-semibold">Resolved by Users:</span>
                  <span className="font-bold text-emerald-400">{analytics.resolvedAlerts.toLocaleString()}</span>
                </div>
              </div>
            </div>

            <div className="p-6 rounded-2xl bg-slate-900 border border-slate-800 space-y-3 text-xs">
              <h3 className="font-bold text-white text-sm">Conversion & Health</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-400">Free to Paid Conversion:</span>
                  <span className="font-bold text-emerald-400">{analytics.subscriptions.conversionRatePercent}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Monthly Churn:</span>
                  <span className="font-bold text-slate-300">{analytics.subscriptions.churnRatePercent}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Database Uptime:</span>
                  <span className="font-bold text-emerald-400">99.99%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab: Content & Editorial Management */}
      {activeTab === 'content' && (
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-800 pb-3">
            <div>
              <h3 className="text-sm font-bold text-white">Editorial Playbook Repository</h3>
              <p className="text-xs text-slate-400">130 total guides ({RESOURCE_ARTICLES.filter(a => !isFutureDate(a.date)).length} published, {RESOURCE_ARTICLES.filter(a => isFutureDate(a.date)).length} scheduled every 2–4 days)</p>
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <input
                type="text"
                value={articleSearch}
                onChange={(e) => setArticleSearch(e.target.value)}
                placeholder="Search articles by title or slug..."
                className="pl-8 pr-3 py-1.5 rounded-xl bg-slate-950 border border-slate-700 text-white text-xs placeholder-slate-500 focus:outline-none focus:border-purple-500"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-slate-800 text-slate-400">
                  <th className="pb-3">Title & Slug</th>
                  <th className="pb-3">Category</th>
                  <th className="pb-3">Author</th>
                  <th className="pb-3">Status</th>
                  <th className="pb-3">Release Date</th>
                  <th className="pb-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800 text-slate-300">
                {filteredContent.map((art) => {
                  const isFuture = isFutureDate(art.date);
                  return (
                    <tr key={art.id} className="hover:bg-slate-800/40 transition-colors">
                      <td className="py-3 font-semibold text-white max-w-xs">
                        <div className="truncate">{art.title}</div>
                        <div className="text-[10px] text-slate-500 font-mono truncate">{art.slug}</div>
                      </td>
                      <td className="py-3">
                        <span className="px-2 py-0.5 rounded text-[10px] bg-slate-800 text-slate-300 uppercase font-bold">
                          {art.category}
                        </span>
                      </td>
                      <td className="py-3 text-slate-300">{art.author.name}</td>
                      <td className="py-3">
                        {isFuture ? (
                          <span className="px-2 py-0.5 rounded text-[10px] bg-purple-950 text-purple-300 border border-purple-800 font-bold flex items-center gap-1 w-max">
                            <Clock className="w-3 h-3" />
                            <span>SCHEDULED</span>
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded text-[10px] bg-emerald-950 text-emerald-300 border border-emerald-800 font-bold flex items-center gap-1 w-max">
                            <CheckCircle2 className="w-3 h-3" />
                            <span>PUBLISHED</span>
                          </span>
                        )}
                      </td>
                      <td className="py-3 font-mono text-[11px] text-slate-400">
                        {art.date}
                      </td>
                      <td className="py-3 text-right">
                        <Link
                          to={`/resources/${art.slug}`}
                          className="px-2.5 py-1 rounded bg-slate-800 hover:bg-slate-700 text-emerald-400 text-[11px] font-semibold inline-flex items-center gap-1"
                        >
                          <span>Inspect</span>
                          <ExternalLink className="w-3 h-3" />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab: Users */}
      {activeTab === 'users' && (
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl space-y-4">
          <div className="flex items-center justify-between border-b border-slate-800 pb-3">
            <h3 className="text-sm font-bold text-white">Registered User Accounts</h3>
            <span className="text-xs text-slate-400">{usersList.length} users</span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-slate-800 text-slate-400">
                  <th className="pb-3">User</th>
                  <th className="pb-3">Role</th>
                  <th className="pb-3">Phone</th>
                  <th className="pb-3">MFA</th>
                  <th className="pb-3">Created</th>
                  <th className="pb-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800 text-slate-300">
                {usersList.map((u) => (
                  <tr key={u.id}>
                    <td className="py-3 font-semibold text-white">
                      <div>{u.firstName} {u.lastName}</div>
                      <div className="text-[11px] text-slate-400 font-mono">{u.email}</div>
                    </td>
                    <td className="py-3">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                        u.role === 'admin' ? 'bg-purple-950 text-purple-300 border border-purple-800' : 'bg-slate-800 text-slate-300'
                      }`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="py-3 font-mono text-[11px]">{u.phone || '—'}</td>
                    <td className="py-3">
                      {u.mfaEnabled ? (
                        <span className="text-emerald-400 font-bold">Enabled</span>
                      ) : (
                        <span className="text-slate-500">Disabled</span>
                      )}
                    </td>
                    <td className="py-3 font-mono text-[11px] text-slate-400">
                      {new Date(u.createdAt).toLocaleDateString('en-AU')}
                    </td>
                    <td className="py-3 text-right space-x-2">
                      <button
                        onClick={() => alert(`View user profile ${u.email}`)}
                        className="px-2.5 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-200 text-[11px]"
                      >
                        Inspect
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab: Breaches */}
      {activeTab === 'breaches' && (
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl space-y-4">
          <div className="flex items-center justify-between border-b border-slate-800 pb-3">
            <h3 className="text-sm font-bold text-white">Known Corporate Breach Repository</h3>
            <button
              onClick={() => setShowAddBreach(true)}
              className="px-3.5 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs flex items-center gap-1.5 transition-colors"
            >
              <PlusCircle className="w-3.5 h-3.5" />
              <span>Add Breach Dataset</span>
            </button>
          </div>

          <div className="divide-y divide-slate-800/80 text-xs">
            {breachesList.map((b) => (
              <div key={b.id} className="py-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-white text-sm">{b.name}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded bg-slate-800 text-slate-300 font-mono">{b.domain}</span>
                  </div>
                  <p className="text-slate-400 text-[11px]">{b.description}</p>
                  <div className="flex flex-wrap gap-1 pt-1">
                    {b.dataClasses.map((dc, i) => (
                      <span key={i} className="px-2 py-0.5 rounded bg-slate-950 border border-slate-800 text-[10px] text-slate-300">
                        {dc}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="text-right flex-shrink-0 text-xs">
                  <div className="text-slate-400">Breach Date: {b.breachDate}</div>
                  <div className="font-mono text-emerald-400 font-bold">{b.pwnCount.toLocaleString()} records</div>
                </div>
              </div>
            ))}
          </div>

          {showAddBreach && (
            <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4">
              <div className="bg-slate-900 border border-slate-700 rounded-3xl p-6 sm:p-8 max-w-md w-full shadow-2xl space-y-4 text-xs">
                <div className="flex items-center justify-between border-b border-slate-800 pb-3">
                  <h4 className="font-bold text-white text-base">Add New Corporate Breach Record</h4>
                  <button onClick={() => setShowAddBreach(false)} className="text-slate-400 hover:text-white">✕</button>
                </div>

                <form onSubmit={handleCreateBreach} className="space-y-3">
                  <div>
                    <label className="block font-semibold text-slate-300 mb-1">Organisation Name:</label>
                    <input
                      type="text"
                      value={newBreachOrg}
                      onChange={(e) => setNewBreachOrg(e.target.value)}
                      placeholder="e.g. Australian Logistics Direct"
                      required
                      className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-white"
                    />
                  </div>

                  <div>
                    <label className="block font-semibold text-slate-300 mb-1">Domain Name:</label>
                    <input
                      type="text"
                      value={newBreachDomain}
                      onChange={(e) => setNewBreachDomain(e.target.value)}
                      placeholder="e.g. auslogistics-direct.com.au"
                      className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-white"
                    />
                  </div>

                  <div>
                    <label className="block font-semibold text-slate-300 mb-1">Breach Incident Date:</label>
                    <input
                      type="date"
                      value={newBreachDate}
                      onChange={(e) => setNewBreachDate(e.target.value)}
                      required
                      className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-white font-mono"
                    />
                  </div>

                  <div>
                    <label className="block font-semibold text-slate-300 mb-1">Exposed Data Classes (comma-separated):</label>
                    <input
                      type="text"
                      value={newBreachClasses}
                      onChange={(e) => setNewBreachClasses(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-white"
                    />
                  </div>

                  <div>
                    <label className="block font-semibold text-slate-300 mb-1">Description / Threat Summary:</label>
                    <textarea
                      rows={2}
                      value={newBreachDesc}
                      onChange={(e) => setNewBreachDesc(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-white"
                    />
                  </div>

                  <div className="flex justify-end gap-2 pt-2">
                    <button
                      type="button"
                      onClick={() => setShowAddBreach(false)}
                      className="px-4 py-2 text-slate-300"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-bold"
                    >
                      Save Breach
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab: Pricing */}
      {activeTab === 'pricing' && (
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl space-y-4">
          <div className="flex items-center justify-between border-b border-slate-800 pb-3">
            <h3 className="text-sm font-bold text-white">Dynamic Pricing Engine</h3>
            <span className="text-xs text-emerald-400 font-mono font-bold">Currency: AUD</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {plans.map((p) => (
              <div key={p.id} className="p-5 rounded-2xl bg-slate-950 border border-slate-800 space-y-3 text-xs">
                <div className="font-bold text-white text-sm">{p.name}</div>
                <div className="space-y-1 text-slate-300">
                  <div>Monthly Price: <span className="font-mono font-bold text-emerald-400">${p.monthlyPrice} AUD</span></div>
                  <div>Annual Price: <span className="font-mono font-bold text-emerald-400">${p.annualPrice} AUD</span></div>
                </div>
                <button
                  onClick={() => alert(`Edit ${p.name} plan configuration.`)}
                  className="w-full py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-semibold"
                >
                  Edit Plan
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tab: Audit */}
      {activeTab === 'audit' && (
        <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl space-y-4">
          <div className="flex items-center justify-between border-b border-slate-800 pb-3">
            <h3 className="text-sm font-bold text-white">System Security & Telemetry Audit Log</h3>
            <span className="text-xs text-slate-400">Live event stream</span>
          </div>

          <div className="divide-y divide-slate-800/80 text-xs">
            {auditLogs.map((log) => (
              <div key={log.id} className="py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div className="space-y-0.5">
                  <div className="font-bold text-white">{log.action}</div>
                  <div className="text-slate-400 text-[11px]">{log.actor} • {log.details}</div>
                </div>
                <div className="text-right text-[11px] text-slate-400 font-mono">
                  <div>{log.location} ({log.ipAddress})</div>
                  <div className="text-slate-500">{new Date(log.timestamp).toLocaleTimeString('en-AU')}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
